import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { navigationItems } from '../../mock';
import {
  LayoutDashboard,
  Users,
  ShoppingCart,
  Plane,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  Package,
  CreditCard,
  Receipt,
  Tag,
  Bell,
  Video,
  Shield,
  Gamepad2,
  ClipboardList,
  Flame,
  ScrollText,
  Wallet,
  Banknote,
  Handshake,
  Smartphone,
  Image,
  FlaskConical,
  UserCheck
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { LucideIcon } from 'lucide-react';
import { Separator } from '../ui/separator';

const iconMap: Record<string, LucideIcon> = {
  layoutDashboard: LayoutDashboard,
  users: Users,
  shoppingCart: ShoppingCart,
  plane: Plane,
  barChart3: BarChart3,
  settings: Settings,
  package: Package,
  creditCard: CreditCard,
  receipt: Receipt,
  tag: Tag,
  bell: Bell,
  video: Video,
  shield: Shield,
  gamepad: Gamepad2,
  clipboardList: ClipboardList,
  flame: Flame,
  scrollText: ScrollText,
  wallet: Wallet,
  banknote: Banknote,
  handshake: Handshake,
  smartphone: Smartphone,
  image: Image,
  flaskConical: FlaskConical,
  userCheck: UserCheck
};

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  isDark: boolean;
}

export default function Sidebar({ collapsed, setCollapsed, isDark }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-screen z-40 transition-all duration-300 ease-in-out flex flex-col',
        collapsed ? 'w-20' : 'w-64',
        isDark ? 'bg-[#0F0F17] border-r border-[#2D2D38]' : 'bg-white border-r border-[#EFEAE0]'
      )}
    >
      {/* Logo */}
      <div className={cn(
        'h-16 flex items-center px-4 border-b',
        isDark ? 'border-[#2D2D38]' : 'border-[#EFEAE0]'
      )}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#F5DC8A] via-[#C9A227] to-[#9C7E1B] flex items-center justify-center shadow-lg shadow-[#C9A227]/30">
            <span className="text-white font-bold text-lg">A</span>
          </div>
          {!collapsed && (
            <span className={cn(
              'font-semibold text-lg tracking-tight',
              isDark ? 'text-white' : 'text-[#111111]'
            )}>
              Админка
            </span>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6 px-3 overflow-y-auto">
        <ul className="space-y-1">
          {navigationItems.map((item) => {
            const Icon = iconMap[item.icon];
            const isActive = location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(item.path));
            const topDivider = item.dividerType === 'top';
            const bottomDivider = item.dividerType === 'bottom';
            return (
              <li key={item.id}>
                {topDivider && <Separator className="my-2" />}
                <button
                  onClick={() => navigate(item.path)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl transition-all duration-200 group',
                    collapsed ? 'justify-center' : '',
                    isActive
                      ? isDark
                        ? 'bg-[#C9A227]/15 text-[#F5DC8A]'
                        : 'bg-[#FBF3D9] text-[#7A5E10] shadow-sm'
                      : isDark
                        ? 'text-slate-400 hover:bg-[#1A1A22] hover:text-white'
                        : 'text-slate-600 hover:bg-[#F5F3EF] hover:text-[#111111]'
                  )}
                >
                  <Icon className={cn(
                    'w-5 h-5 flex-shrink-0',
                    isActive
                      ? isDark
                        ? 'text-[#F5DC8A]'
                        : 'text-[#9C7E1B]'
                      : ''
                  )} />
                  {!collapsed && (
                    <span className="font-medium text-sm">{item.label}</span>
                  )}
                </button>
                {bottomDivider && <Separator className="my-2" />}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Collapse button */}
      <div className={cn(
        'p-3 border-t',
        isDark ? 'border-[#2D2D38]' : 'border-[#EFEAE0]'
      )}>
        <button
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Развернуть боковую панель' : 'Свернуть боковую панель'}
          className={cn(
            'w-full flex items-center justify-center gap-2 px-3 py-2 rounded-2xl transition-all duration-200',
            isDark
              ? 'text-slate-400 hover:bg-[#1A1A22] hover:text-white'
              : 'text-slate-500 hover:bg-[#F5F3EF] hover:text-[#111111]'
          )}
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Свернуть</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}

